import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import _ from 'lodash';

import {
  LoanApplicationTabName,
  LoanApplicationTabRouteParamInverted,
} from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { selectEntityNameMap } from 'store/loanApplications/selectors/presenceEntityNames.selectors';
import { updateLocationAction } from 'store/presence/actions';
import { selectIsPresenceConnected } from 'store/presence/selectors';

import { LOCATION_UPDATE_DEBOUNCE_MS } from './constants';
import { buildLocationMetadata } from './utils';

export const usePresenceLocationUpdate = () => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { tab: activeTab, subtab, entityId, section, subsection } = useParams();

  const isPresenceConnected = useSelector(selectIsPresenceConnected);
  const entityNameMap = useSelector(selectEntityNameMap);

  // Ref: always-fresh value without triggering the effect (feedback loop safety net)
  const entityNameMapRef = useRef(entityNameMap);
  entityNameMapRef.current = entityNameMap;

  // Derived state — computed during render, not inside the effect
  const metadata = useMemo(() => {
    if (!activeTab) {
      return null;
    }

    const tabKey = LoanApplicationTabRouteParamInverted[activeTab];
    const tabName = LoanApplicationTabName[tabKey] || activeTab;
    const encodedSectionId = subtab ? `${subtab}::${section ?? ''}` : section;

    return buildLocationMetadata({
      tab: tabName,
      sectionId: encodedSectionId,
      entityId,
      subsection,
      subtab,
      entityNameMap: entityNameMapRef.current,
    });
  }, [activeTab, subtab, entityId, section, subsection]);

  // useRef instead of useMemo for debounce instance (dispatch is stable)
  const debouncedUpdateLocationRef = useRef(
    _.debounce(
      ({ currentLoanId, locationMetadata }) => {
        dispatch(updateLocationAction({ entityId: currentLoanId, metadata: locationMetadata }));
      },
      LOCATION_UPDATE_DEBOUNCE_MS,
      { leading: true, trailing: true },
    ),
  );

  useEffectSafeStrictMode(() => {
    if (!isPresenceConnected || !loanId || !metadata) {
      return;
    }

    const debouncedFn = debouncedUpdateLocationRef.current;

    debouncedFn({ currentLoanId: loanId, locationMetadata: metadata });

    return () => {
      debouncedFn.cancel();
    };
  }, [isPresenceConnected, loanId, metadata]);
};
